import {
  Component,
  OnInit,
  PendingTasks,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  NodeProcom,
  NodeProcov,
  NodeProduct,
  NodeProser,
  NodeProam,
  Rating,
} from '@app/models/entity.model';
import { ProductAdditionalSegmentComponent } from '@app/components/product/product-additional-segment/product-additional-segment.component';
import { ParagraphLoaderComponent } from '@app/entities/paragraph/loader.component';
import { ProductDamageExamplesComponent } from '@app/components/product/product-damage-examples/product-damage-examples.component';
import { ProductFaqComponent } from '@app/components/product/product-faq/product-faq.component';
import { ProductHeroComponent } from '@app/components/product/product-hero/product-hero.component';
import { ProductInShortComponent } from '@app/components/product/product-in-short/product-in-short.component';
import { ProductUspComponent } from '@app/components/product/product-usp/product-usp.component';
import { RecommendationsComponent } from '@app/components/recommendations/recommendations.component';
import { ParagraphPgPnav } from '@app/models/paragraph.model';
import { TextsService } from '@app/services/texts.service';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { ProductRelationComponent } from '@app/components/product/product-relation/product-relation.component';
import { ProductHowToProceedComponent } from '@app/components/product/product-how-to-proceed/product-how-to-proceed.component';
import { DOCUMENT } from '@angular/common';
import { MobiLdSchemaComponent } from '@app/components/mobi-ld-schema/mobi-ld-schema.component';
import { DataService } from '@app/services/data.service';
import { CalcCtaPipe } from '@app/pipes/calc-cta.pipe';
import { ORG_NAMES } from '@app/constants';
import { DrupalService } from '@app/services/drupal.service';
import { ApiService } from '@app/services/api.service';
import { ParagraphPgPpsComponent } from '@app/entities/paragraph/paragraph-pg-pps/paragraph-pg-pps.component';

@Component({
  selector: 'mobi-product-layout',
  templateUrl: './product-layout.component.html',
  styleUrls: ['./product-layout.component.scss'],
  standalone: true,
  imports: [
    ComponentSharedModule,
    ProductAdditionalSegmentComponent,
    ProductDamageExamplesComponent,
    ProductFaqComponent,
    ProductHeroComponent,
    ProductInShortComponent,
    ProductUspComponent,
    RecommendationsComponent,
    ProductRelationComponent,
    ProductHowToProceedComponent,
    MobiLdSchemaComponent,
    CalcCtaPipe,
    ParagraphLoaderComponent,
    ParagraphPgPpsComponent,
  ],
})
export class ProductLayoutComponent implements OnInit {
  readonly entity = input.required<
    NodeProduct | NodeProcom | NodeProser | NodeProcov | NodeProam
  >();
  readonly hasOverview = input<boolean>(true);
  readonly isAssetManagement = input<boolean>(false);
  readonly contentLinksOverride = input<ParagraphPgPnav[] | null>(null);
  contentLinks = signal<ParagraphPgPnav[]>([]);

  #doc = inject(DOCUMENT);
  #texts = inject(TextsService);
  #task = inject(PendingTasks);

  rating = signal<Rating>({ average_rating: 0, count: 0, ratingPageUrl: undefined });
  #data = inject(DataService);
  #drupal = inject(DrupalService);
  #api = inject(ApiService);
  orgNames: any = ORG_NAMES;
  rokkaHash = computed(() => this.entity()?.headerImage?.image?.entity?.rokka?.hash);

  isProtektaProduct = computed(
    () =>
      this.entity().taxonomyProduct?.some((taxP) => taxP.label === 'Protekta Product') ??
      false
  );

  /** Extract calculatorCtaPrer - only available on NodeProduct */
  calculatorCtaPrer = computed(() => {
    const e = this.entity();
    return 'calculatorCtaPrer' in e ? (e as NodeProduct).calculatorCtaPrer : undefined;
  });

  imageUrl = computed(() => {
    const rokkaHash = this.rokkaHash();
    const filename = this.entity()?.headerImage?.filename;
    if (!rokkaHash || !filename) return '';
    return `${this.#api.rokkaUrl}${rokkaHash}/${filename}`;
  });

  schema = computed(() => {
    if (this.entity().schemaOrg) return this.entity().schemaOrg;
    const schema: any = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: this.entity().title,
      image: this.imageUrl(),
      description: this.entity().teaserText || this.entity().lead,
      brand: {
        '@type': 'Brand',
        name: this.orgNames[`${this.#drupal.language() as any}`],
      },
    };
    if (this.rating().count > 0) {
      schema['aggregateRating'] = {
        '@type': 'AggregateRating',
        ratingValue: this.rating().average_rating,
        ratingCount: this.rating().count,
      };

      if (this.rating().highest_rating) {
        schema['aggregateRating']['bestRating'] = this.rating().highest_rating;
      }

      if (this.rating().lowest_rating) {
        schema['aggregateRating']['worstRating'] = this.rating().lowest_rating;
      }
    }
    return schema;
  });

  isLoading = signal(false);

  async ngOnInit() {
    const cleanup = this.#task.add();
    await this.getContentLinks();

    if (this.entity().productRatingPage?.product?.id) {
      try {
        this.isLoading.set(true);
        const rating: any = await this.#data.getProductRating(
          this.entity().productRatingPage.product.id
        );
        if (!rating || rating.count === 0) {
          this.rating.set({ average_rating: 0, count: 0 });
        } else {
          rating.ratingPageUrl = this.entity().productRatingPage.url.path;
          this.rating.set(rating);
        }
      } catch (error) {
        this.rating.set({ average_rating: 0, count: 0 });
      }
    }

    this.isLoading.set(false);
    cleanup();
  }

  async getContentLinks() {
    if (this.contentLinksOverride()) {
      this.contentLinks.set(this.contentLinksOverride()!);
      return;
    }
    /*
    check for all structured content if it is
    available and show the content links for the resp. paragraphs
     */

    const promises: Promise<string>[] = [];

    if (this.entity().inShortTitle) {
      promises.push(
        this.#texts.getText(
          'insurance_in_short_name',
          'frontend',
          'Versicherung in Kürze'
        )
      );
      promises.push(
        this.#texts.getText(
          'insurance_in_short_paramenter_name',
          'frontend',
          'versicherung_in_kuerze'
        )
      );
    }
    if (this.entity().damageExamples && this.entity().damageExamples!.length > 0) {
      if (this.entity().damageTitle) {
        promises.push(Promise.resolve(this.entity().damageTitle || ''));
      } else {
        promises.push(
          this.#texts.getText('damage_examples_name', 'frontend', 'Schadenbeispiele')
        );
      }

      promises.push(
        this.#texts.getText(
          'damage_examples_paramenter_name',
          'frontend',
          'schadenbeispiele'
        )
      );
    }

    const packages = this.entity().packages;
    if (packages?.anchorLink && packages.packages?.length) {
      const link: ParagraphPgPnav = {
        label: packages.anchorLink,
        sectionId: packages.anchorLinkUrl || packages.id,
      };
      promises.push(Promise.resolve(link.label || ''));
      promises.push(Promise.resolve(link.sectionId || ''));
    }

    if (this.entity().faqTitle) {
      promises.push(this.#texts.getText('faqs_name', 'frontend', 'Fragen & Antworten'));
      promises.push(this.#texts.getText('faqs_paramenter_name', 'frontend', 'faqs'));
    }
    if (this.entity().promotedContent && this.entity().promotedContent!.length > 0) {
      promises.push(
        this.#texts.getText('promoted_content_name', 'frontend', 'Weitere Vorteile')
      );
      promises.push(
        this.#texts.getText(
          'promoted_content_paramenter_name',
          'frontend',
          'weitere-vorteile'
        )
      );
    }

    if (this.entity().furtherProducts && this.entity().furtherProducts!.length > 0) {
      promises.push(
        this.#texts.getText(
          'product_further_products_name',
          'frontend',
          'Weitere Produkte'
        )
      );
      promises.push(
        this.#texts.getText(
          'product_further_products_paramenter_name',
          'frontend',
          'weitere-produkte'
        )
      );
    }

    await Promise.all(promises)
      .then((texts) => {
        for (let i = 0; i < texts.length - 1; i += 2) {
          const link: ParagraphPgPnav = {
            label: texts[i],
            sectionId: texts[i + 1],
          };
          this.contentLinks.update((values: any) => [...values, link]);

          if (this.isAssetManagement() && this.entity()?.paragraphs?.length) {
            this.entity()
              .paragraphs!.filter((pg: { anchorLink: any }) => pg.anchorLink)
              .map((pg: { anchorLink: any; id: any }) => {
                const link: ParagraphPgPnav = {
                  label: pg.anchorLink,
                  sectionId: pg.id,
                };
                this.contentLinks.update((values: any) => [...values, link]);
              });
          }
        }
      })
      .catch((e) => this.#handleError(e));
  }

  #handleError(e: any) {
    console.error(e);
    this.#doc.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }
}
